import React from "react";
var __jsx = React.createElement;
import { Fragment, useEffect, useState, createContext, useContext, useCallback, useMemo, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import IconButton from '@modules/shared/components/buttons/icon-button';
import Icons from '../../../icons';
import useCssModules from '@lib/hooks/use-css-modules';

// Context for managing side panel stack

const SidePanelStackContext = /*#__PURE__*/createContext(null);

// Provider component for managing the panel stack
export const SidePanelStackProvider = _ref => {
  let {
    children
  } = _ref;
  const {
    0: panelStack,
    1: setPanelStack
  } = useState([]);
  const registerPanel = useCallback(id => {
    setPanelStack(prev => {
      // If the panel is already at the top, don't update
      if (prev[prev.length - 1] === id) {
        return prev;
      }
      // Remove the panel if it exists and add it to the end
      return [...prev.filter(panelId => panelId !== id), id];
    });
  }, []);
  const unregisterPanel = useCallback(id => {
    setPanelStack(prev => prev.filter(panelId => panelId !== id));
  }, []);
  const isTopPanel = useCallback(id => {
    return panelStack[panelStack.length - 1] === id;
  }, [panelStack]);
  const closeTopPanel = useCallback(() => {
    // This will be handled by the individual panels
  }, []);
  const contextValue = useMemo(() => ({
    panelStack,
    registerPanel,
    unregisterPanel,
    isTopPanel,
    closeTopPanel
  }), [panelStack, registerPanel, unregisterPanel, isTopPanel, closeTopPanel]);
  return __jsx(SidePanelStackContext.Provider, {
    value: contextValue
  }, children);
};

// Hook to use the side panel stack context
const useSidePanelStack = () => {
  const context = useContext(SidePanelStackContext);
  return context; // Return null if not within provider (backwards compatibility)
};
const MuscleSideOverPanel = _ref2 => {
  let {
    openState,
    setOpenState,
    title,
    useBackIcon,
    children,
    secondaryButtonText,
    onSecondaryButtonClick,
    disableSecondaryButton,
    panelId
  } = _ref2;
  const {
    cssModules
  } = useCssModules();
  const sidePanelStack = useSidePanelStack();
  const sidePanelStackRef = useRef(sidePanelStack);

  // Keep ref updated
  useEffect(() => {
    sidePanelStackRef.current = sidePanelStack;
  }, [sidePanelStack]);

  // Generate a unique ID if not provided
  const {
    0: uniqueId
  } = useState(() => panelId || "panel-".concat(Math.random().toString(36).substr(2, 9)));

  // Register/unregister panel with stack when open state changes
  useEffect(() => {
    if (sidePanelStackRef.current) {
      if (openState) {
        sidePanelStackRef.current.registerPanel(uniqueId);
      } else {
        sidePanelStackRef.current.unregisterPanel(uniqueId);
      }
    }
  }, [openState, uniqueId]); // Remove sidePanelStack from dependencies

  // Handle close action - only close if this is the top panel or if no stack management
  const handleClose = useCallback(value => {
    const currentStack = sidePanelStackRef.current;
    if (currentStack) {
      // If using stack management, only close if this is the top panel
      if (!value && currentStack.isTopPanel(uniqueId)) {
        setOpenState(false);
      } else if (value) {
        setOpenState(true);
      }
      // If this is not the top panel, ignore the close request
    } else {
      // No stack management, use original behavior
      setOpenState(value);
    }
  }, [setOpenState, uniqueId]); // Remove sidePanelStack from dependencies

  // Handle manual close button click
  const handleManualClose = useCallback(() => {
    setOpenState(false);
  }, [setOpenState]);
  return __jsx(Transition.Root, {
    show: openState,
    as: Fragment
  }, __jsx(Dialog, {
    as: "div",
    className: "relative z-[1999]",
    onClose: handleClose
  }, __jsx(Transition.Child, {
    as: Fragment,
    enter: "ease-in-out duration-500",
    enterFrom: "opacity-0",
    enterTo: "opacity-100",
    leave: "ease-in-out duration-500",
    leaveFrom: "opacity-100",
    leaveTo: "opacity-0"
  }, __jsx("div", {
    className: "fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity",
    style: {
      top: 'var(--header-height, 80px)'
    }
  })), __jsx("div", {
    className: "fixed overflow-hidden",
    style: {
      top: 'var(--header-height, 80px)',
      left: 0,
      right: 0,
      bottom: 0
    }
  }, __jsx("div", {
    className: "absolute inset-0 overflow-hidden"
  }, __jsx("div", {
    className: "pointer-events-none fixed inset-y-0 left-0 flex max-w-full"
  }, __jsx(Transition.Child, {
    as: Fragment,
    enter: "transform transition ease-in-out duration-500 sm:duration-700",
    enterFrom: "-translate-x-full",
    enterTo: "translate-x-0",
    leave: "transform transition ease-in-out duration-500 sm:duration-700",
    leaveFrom: "translate-x-0",
    leaveTo: "-translate-x-full"
  }, __jsx(Dialog.Panel, {
    className: "pointer-events-auto w-screen lg:w-fit"
  }, __jsx("div", {
    className: "h-full flex-col overflow-y-scroll bg-white py-6"
  }, __jsx("div", {
    className: cssModules === null || cssModules === void 0 ? void 0 : cssModules.globals.sideOverPanel.mainContainer
  }, __jsx(React.Fragment, null, __jsx("div", {
    className: "flex items-center justify-between pb-5 sm:px-6"
  }, __jsx("div", {
    className: "flex flex-row items-center justify-center gap-2"
  }, __jsx(IconButton, {
    icon: useBackIcon ? __jsx(Icons.backArrowMobile, {
      className: cssModules === null || cssModules === void 0 ? void 0 : cssModules.globals.sideOverPanel.backIconClassName,
      "aria-hidden": "true"
    }) : __jsx(XMarkIcon, {
      className: cssModules === null || cssModules === void 0 ? void 0 : cssModules.globals.sideOverPanel.exitIconClassName,
      "aria-hidden": "true"
    }),
    onClickButton: handleManualClose
  }), __jsx(Dialog.Title, {
    className: cssModules === null || cssModules === void 0 ? void 0 : cssModules.globals.sideOverPanel.titleClassName
  }, __jsx("span", {
    id: "side-over-panel-title",
    className: "cursor-pointer",
    onClick: handleManualClose
  }, title || ''))), secondaryButtonText && onSecondaryButtonClick && __jsx("button", {
    onClick: onSecondaryButtonClick,
    disabled: disableSecondaryButton
  }, __jsx("div", {
    className: "flex gap-2"
  }, __jsx(Icons.trashcanV2, {
    size: 24,
    className: cssModules === null || cssModules === void 0 ? void 0 : cssModules.globals.sideOverPanel.clearIconClassName
  }), __jsx("span", {
    className: cssModules === null || cssModules === void 0 ? void 0 : cssModules.globals.sideOverPanel.clearTextClassName
  }, secondaryButtonText)))), children))))))))));
};
export default MuscleSideOverPanel;